// ADENDA z48-8 (Diego, 28-oct-2026): banda grande «ACTUALIZAR A LA VERSIÓN NUEVA» en una cotización guardada a la que le faltan
// los parámetros que hoy nacen prendidos (compartido → dominio/version.ts). El servidor calcula EN SECO y aquí se enseña el
// antes/después (opciones, paneles, contado, ahorro y retorno, y la tarifa si cambia; el margen solo a quien ve costos: el servidor lo manda en null a los demás). Nada
// cambia hasta «Confirmar»: queda en el historial y el PDF anterior queda viejo.
// pulido72 (29-oct): la banda sale SOLO si actualizar cambia algo de ESTA cotización (el servidor lo calcula en seco al abrirla:
// `cambia`, y dice cuáles parámetros: `con_efecto`). En Hermosillo, donde el sol del municipio no cambia nada, ya no sale.
import { useEffect, useState } from 'react';
import { ArrowRight, Check, RefreshCw, Sparkles } from 'lucide-react';
import { AVISO_CASA_FIJADO_EN_24, fechaDeVersion, fechaLarga, ladoAntesDespues, parametrosDeVersionQueFaltan, pdfDetenidoPor, rojoAisladoDeOtraVersion } from '@cotizador/compartido';
import type { LadoAntesDespues, OpcionAntesDespues, OpcionId, RespuestaActualizarVersion } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { usarUsuario } from '../../sesion.tsx';
import { Boton, ErrorEnLinea } from '../../componentes/ui.tsx';
import { ETIQUETA_OPCION, ETIQUETA_SEMAFORO, dinero, kwp } from '../../logica/etiquetas.ts';
import { usarCotizacion } from './contexto.tsx';

/** Una ganada, perdida o cancelada ya no se mueve: la banda no se ofrece ahí. */
const ESTADOS_CERRADOS: readonly string[] = ['ganada', 'perdida', 'cancelada'];

function Celda({ o, aislado }: { o: OpcionAntesDespues | undefined; aislado: boolean }) {
  if (!o) return <td className="tabla__secundario">No se ofrece</td>;
  return (
    <td>
      <span className="tabla__principal">{dinero(o.contado)}</span>
      <span className="tabla__secundario">
        {' '}
        · {o.paneles} paneles · {kwp(o.kwp)}
        {o.semaforo && ` · ${ETIQUETA_SEMAFORO[o.semaforo]}`}
        {o.margen_pct !== null && ` · ${o.margen_pct.toLocaleString('es-MX', { maximumFractionDigits: 1 })} %`}
      </span>
      {/* Verificación del 28-oct: con la versión nueva puede cambiar el ahorro y el retorno sin mover paneles ni precio. hoy74: un
          aislado no tiene ahorro de recibo (ADENDA z50): solo su retorno, que es contra el generador de diésel. */}
      <span className="tabla__secundario">
        {' '}
        · {aislado ? '' : `ahorra ${dinero(o.ahorro_anual)} al año · `}
        {o.retorno_anios === null ? 'sin retorno' : `${aislado ? 'se paga en ' : ''}${o.retorno_anios.toLocaleString('es-MX', { maximumFractionDigits: 1 })} años${aislado ? ' contra el generador de diésel' : ''}`}
      </span>
    </td>
  );
}

const ORDEN: readonly OpcionId[] = ['arranque', 'exacta', 'holgada'];

export function TablaAntesDespues({ antes, despues }: { antes: LadoAntesDespues | null; despues: LadoAntesDespues | null }) {
  const ids = ORDEN.filter((id) => antes?.opciones.some((o) => o.id === id) || despues?.opciones.some((o) => o.id === id));
  const nombre = (id: OpcionId) => `${ETIQUETA_OPCION[id]}${despues?.recomendada === id ? ' (recomendada)' : ''}`;
  return (
    <div className="tabla-envoltura">
      <table className="tabla tabla--compacta" aria-label="Antes y después">
        <thead>
          <tr>
            <th scope="col">Opción</th>
            <th scope="col">Antes</th>
            <th scope="col">Después</th>
          </tr>
        </thead>
        <tbody>
          {ids.map((id) => (
            <tr key={id}>
              <th scope="row">{nombre(id)}</th>
              <Celda o={antes?.opciones.find((o) => o.id === id)} aislado={antes?.aislado ?? despues?.aislado ?? false} />
              <Celda o={despues?.opciones.find((o) => o.id === id)} aislado={despues?.aislado ?? antes?.aislado ?? false} />
            </tr>
          ))}
          {antes && despues && antes.tarifa !== despues.tarifa && (
            <tr>
              <th scope="row">Tarifa con paneles</th>
              <td>{antes.tarifa}</td>
              <td className="tabla__principal">{despues.tarifa}</td>
            </tr>
          )}
          <tr>
            <th scope="row">Lo que cobra el PDF</th>
            <td className="tabla__principal">{antes?.contado_recomendada != null ? dinero(antes.contado_recomendada) : 'Sin precio'}</td>
            <td className="tabla__principal">{despues?.contado_recomendada != null ? dinero(despues.contado_recomendada) : 'Sin precio'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function BandaActualizarVersion() {
  const { cot, ejecutar } = usarCotizacion();
  const { puedeEn } = usarUsuario();
  // El cálculo en seco de ESTA revisión (al abrirla y después de cada cambio): la banda depende de él.
  const [seco, setSeco] = useState<{ clave: string; r: RespuestaActualizarVersion | null; error: unknown } | null>(null);
  const [abierta, setAbierta] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const faltan = parametrosDeVersionQueFaltan(cot);
  const ofrece = faltan.length > 0 && !cot.papelera && puedeEn('editar_cotizaciones', cot) && !ESTADOS_CERRADOS.includes(cot.estado);
  const clave = `${cot.id}:${cot.revision}`;
  useEffect(() => {
    if (!ofrece) return;
    let vigente = true;
    const pedir = () =>
      api.actualizarVersion(cot.id, cot.revision, false).then(
        (r) => vigente && setSeco({ clave, r, error: null }),
        (e: unknown) => vigente && setSeco({ clave, r: null, error: e }),
      );
    // Al abrirla, de inmediato; después de un cambio, cuando se calma (varios guardados seguidos piden UN cálculo en seco).
    const espera = seco?.clave.startsWith(`${cot.id}:`) ? 400 : 0;
    const t = setTimeout(() => void pedir(), espera);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
    // la clave ya trae id y revisión
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ofrece, clave]);
  // Mientras llega el de la revisión nueva se queda el último de esta cotización (sin parpadeo); sin ninguno, nada.
  const ultimo = seco && seco.clave.startsWith(`${cot.id}:`) ? seco : null;
  if (!ofrece || !ultimo) return null;
  const vista = ultimo.r;
  // No cambia nada para esta cotización: no hay banda (ni un PDF que quede viejo sin motivo). Un servidor de antes de pulido72 no
  // manda `cambia`: la banda de siempre.
  if (vista && vista.cambia === false) return null;
  const alDia = ultimo.clave === clave;
  const correr = async (f: () => Promise<void>) => {
    setOcupado(true);
    setError(null);
    try {
      await f();
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  };
  // Sin el cálculo en seco (falló): la banda de antes, con el error a la vista; el botón lo vuelve a pedir.
  const reintentar = () =>
    correr(async () => {
      setSeco({ clave, r: await api.actualizarVersion(cot.id, cot.revision, false), error: null });
      setAbierta(true);
    });
  const confirmar = () =>
    correr(async () => {
      await ejecutar((c) => api.actualizarVersion(c.id, c.revision, true));
      setAbierta(false);
    });
  const textos = vista?.con_efecto?.length ? vista.con_efecto : faltan;
  const fecha = vista ? vista.version_del : fechaDeVersion(faltan);
  return (
    <section className="aviso-ancho aviso-ancho--ambar" aria-label="Actualizar a la versión nueva">
      <div className="pila">
        <p>
          <strong>Esta cotización se hizo con una versión anterior del cotizador.</strong> La versión del {fechaLarga(fecha)} trae:{' '}
          {textos.map((x) => x.texto).join('; ')}. Nada cambia hasta que lo confirmes.
        </p>
        {!(abierta && vista) && (
          <div className="fila-botones">
            <Boton variante="principal" icono={<Sparkles aria-hidden />} cargando={ocupado} onClick={() => (vista ? setAbierta(true) : void reintentar())}>
              Actualizar a la versión nueva
            </Boton>
          </div>
        )}
        {abierta && vista && (
          <>
            {vista.error ? (
              <p className="texto-rojo">Con la versión nueva no se pudo calcular: {vista.error}. Se queda como está.</p>
            ) : (
              <TablaAntesDespues antes={vista.antes} despues={vista.despues} />
            )}
            {vista.tambien_tabla && <p className="nota-suave">Parte del cambio es de la tabla de precios vigente (al recalcular ya cambiaba), no solo de la versión.</p>}
            <p className="nota-suave">Al confirmar queda en el historial y el PDF anterior queda como viejo: genera la versión nueva en «PDF» antes de mandarla.</p>
            <div className="fila-botones">
              <Boton variante="fantasma" onClick={() => setAbierta(false)}>
                Dejarla como está
              </Boton>
              <Boton variante="principal" icono={<ArrowRight aria-hidden />} iconoDespues cargando={ocupado || !alDia} disabled={vista.error !== null || !alDia} onClick={() => void confirmar()}>
                Confirmar
              </Boton>
            </div>
          </>
        )}
        <ErrorEnLinea
          error={error ?? ultimo.error}
          alCerrar={() => {
            setError(null);
            if (ultimo.error) setSeco({ ...ultimo, error: null });
          }}
        />
      </div>
    </section>
  );
}

/** hoy87 (ADENDA z62-4; verificación de hoy87, 29-oct): un AISLADO sin equipos guardado con el rojo «las baterías de la Holgada
 *  durarían de 72 a 145 h… Mientras tanto el PDF no sale» que calculó un motor ANTERIOR a la regla de las 12 h. Abrirlo no recalcula,
 *  así que el rojo guardado seguía apagando «Generar PDF» y «Ir a arreglarlo» mandaba a capturar equipos, justo lo que z62-4 quería
 *  evitar; solo se destrababa si alguien tocaba «Recalcular» en Opciones, y nada se lo decía. Esta banda lo dice y lo hace: recalcula
 *  con la regla de hoy (POST …/calcular, queda en el historial) y enseña el antes y el después de lo que cobra el PDF. Si el rojo
 *  sigue (ni repartiendo la casa en 12 h caben sus baterías), lo dice. Ya recalculada, su versión es la de hoy y la banda no vuelve. */
export function BandaRojoDeOtraVersion() {
  const { cot, ejecutar } = usarCotizacion();
  const { puedeEn, veCostos, veSemaforo } = usarUsuario();
  const [hecho, setHecho] = useState<{ id: string; antes: LadoAntesDespues | null; despues: LadoAntesDespues | null; sigue: string | null; porFijado: 'no-cabe' | 'con-fijado' | null } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const ve = { costos: veCostos, semaforo: veSemaforo };
  if (hecho && hecho.id === cot.id)
    return (
      <section className="aviso-ancho aviso-ancho--info" aria-label="Recalculada con la regla de hoy">
        <div className="pila">
          <p>
            <strong>Listo: se recalculó con la regla de hoy.</strong>{' '}
            {hecho.sigue
              ? hecho.porFijado === 'no-cabe'
                ? `El rojo sigue por las baterías o el equipo que fijaste en Opciones: con ellos no caben ni repartiendo toda la casa en 12 h. ${hecho.sigue}`
                : hecho.porFijado === 'con-fijado'
                  ? `El rojo sigue con las baterías o el equipo que fijaste en Opciones. ${hecho.sigue}`
                  : `El rojo sigue: ni repartiendo toda la casa en 12 h caben sus baterías. ${hecho.sigue}`
              : 'El rojo se fue: ya puedes generar el PDF. Revisa los precios de hoy antes de mandarla.'}
          </p>
          <TablaAntesDespues antes={hecho.antes} despues={hecho.despues} />
          <div className="fila-botones">
            <Boton variante="fantasma" icono={<Check aria-hidden />} onClick={() => setHecho(null)}>
              Entendido
            </Boton>
          </div>
        </div>
      </section>
    );
  const b = cot.borrador;
  if (!rojoAisladoDeOtraVersion(b) || cot.papelera || !puedeEn('editar_cotizaciones', cot) || ESTADOS_CERRADOS.includes(cot.estado)) return null;
  const recalcular = async () => {
    setOcupado(true);
    setError(null);
    const antes = ladoAntesDespues(b, ve);
    try {
      const r = await ejecutar((c) => api.calcular(c.id, c.revision));
      const d = r.cotizacion.borrador;
      // hoy97, verificación: con la Arranque recomendada y algo fijado en Opciones que no cabe en 12 h, el motor deja la casa en 24 h
      // con su amarillo; el rojo que sigue es por lo fijado (no «ni en 12 h caben»).
      // hoy97, ronda 3: con algo fijado y SIN ese amarillo (Guaymas 400 con la Arranque recomendada y 8 baterías en la Holgada: sin lo fijado cabe en
      // 24 h y el motor ni intenta las 12 h), «ni repartiendo en 12 h caben» tampoco es cierto: solo se dice que el rojo sigue con lo fijado.
      const hayFijado = !!d.parametros?.equipo_respaldo || Object.values(d.parametros?.modulos_respaldo ?? {}).some((m) => m != null);
      const porFijado = d.avisos.some((a) => a.nivel === 'amarillo' && !a.resuelto && a.mensaje.startsWith(AVISO_CASA_FIJADO_EN_24)) ? 'no-cabe' : hayFijado ? 'con-fijado' : null;
      setHecho({ id: cot.id, antes, despues: ladoAntesDespues(d, ve), sigue: pdfDetenidoPor(d.avisos), porFijado });
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  };
  return (
    <section className="aviso-ancho aviso-ancho--ambar" aria-label="Recalcular con la regla de hoy">
      <div className="pila">
        <p>
          <strong>Este rojo es de una versión anterior del cotizador.</strong> Hoy, un aislado sin equipos cuyas baterías no caben con toda la casa en 24 h la reparte en 12 h y su PDF sale (cambian precios y
          horas). Recalcúlala para ver los de hoy: si ya cabe, el rojo se va; si no, te dice qué falta.
        </p>
        <div className="fila-botones">
          <Boton variante="principal" icono={<RefreshCw aria-hidden />} cargando={ocupado} onClick={() => void recalcular()}>
            Recalcular con la regla de hoy
          </Boton>
        </div>
        <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
      </div>
    </section>
  );
}
